import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { format, isToday } from "date-fns";
import { es } from "date-fns/locale";
import { compareDateOnly } from "../../shared/utils/dateUtils";
import { tooltipSurfaceSx } from "../../shared/styles/tooltipSurface";
import { DailyOccupancy } from "../utils/dailyOccupancy";

interface DayColumnProps {
  dia: Date;
  width: number;
  occupancy?: DailyOccupancy;
}

const DayColumn: React.FC<DayColumnProps> = ({ dia, width, occupancy }) => {
  const theme = useTheme();
  const esHoy = isToday(dia);
  const esFinDeSemana = dia.getDay() === 0 || dia.getDay() === 6;
  const esPasado = compareDateOnly(dia, new Date()) < 0;
  const todayBg = alpha(
    theme.palette.primary.main,
    theme.palette.mode === "dark" ? 0.42 : 0.26
  );
  const textColor = esHoy
    ? "primary.main"
    : esPasado
    ? "text.disabled"
    : "text.secondary";
  const numberColor = esHoy
    ? "primary.main"
    : esPasado
    ? "text.disabled"
    : "text.primary";
  const pillOpacity = esPasado && !esHoy ? 0.58 : 1;
  const freeDepartments = occupancy
    ? Math.max(occupancy.available - occupancy.occupied, 0)
    : 0;
  const occupancyTooltip = occupancy
    ? `Ocupación: ${occupancy.occupied}/${occupancy.available} · Libres: ${freeDepartments} · Fuera de servicio: ${occupancy.blocked} · Entradas: ${occupancy.checkins} · Salidas: ${occupancy.checkouts}`
    : "";

  return (
    <Box
      sx={{
        width: width,
        minWidth: width,
        height: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        borderRight: 1,
        borderColor: "divider",
        bgcolor: esHoy
          ? todayBg
          : esFinDeSemana
          ? theme.palette.action.hover
          : theme.palette.background.paper,
        color: esPasado && !esHoy ? "text.disabled" : "inherit",
        position: "relative",
        pt: 0.45,
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "1px",
          bgcolor: "divider",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.7rem",
          lineHeight: 1.1,
          color: textColor,
          mb: 0.25,
          fontWeight: esHoy ? 800 : 700,
        }}
      >
        {format(dia, "EEE", { locale: es })}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.88rem",
          lineHeight: 1.15,
          fontWeight: esHoy ? 800 : 500,
          color: numberColor,
        }}
      >
        {format(dia, "d")}
      </Typography>
      {occupancy && occupancy.total > 0 && (
        <Tooltip
          title={occupancyTooltip}
          arrow
          slotProps={{
            tooltip: {
              sx: tooltipSurfaceSx,
            },
            arrow: {
              sx: {
                color: tooltipSurfaceSx.bgcolor,
              },
            },
          }}
        >
          <Box
            component="span"
            aria-label={occupancyTooltip}
            sx={{
              position: "absolute",
              bottom: 5,
              minWidth: 30,
              height: 15,
              px: 0.45,
              border: "1px solid",
              borderColor: esHoy
                ? alpha(theme.palette.primary.main, 0.72)
                : theme.palette.divider,
              borderRadius: 999,
              color: numberColor,
              fontSize: "0.62rem",
              lineHeight: "13px",
              textAlign: "center",
              bgcolor: "transparent",
              opacity: pillOpacity,
              userSelect: "none",
            }}
          >
            {occupancy.percentage}%
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default DayColumn;
